import { useCallback, useEffect, useRef, useState } from 'react';
import useSlingApi from '@sdk/api/useSlingApi';
import {
  SaveClientFlags,
  SetupOrgOnboardingSteps,
  ToastBusinessCategory,
} from '@sdk/authentication/types';
import { GroupTypes } from '@sdk/authentication/types/apiTypes';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { LocationType } from '@sdk/locations/types/LocationType';
import { IntegrationType } from '@sdk/integrations/types';

import { TOAST_ONBOARDING_CATEGORIES, DEFAULT_STEPS_PATH } from '@web/onboarding/constants';
import { sendPosFeedback } from '@web/onboarding/helpers/posFeedback';
import { PointOfSale } from '@web/onboarding/types';
import { useSteps } from '@web/onboarding/steps/hooks/useSteps';
import { useOnboardingFlags } from '@web/onboarding/hooks/useOnboardingFlags';

type Props = {
  user: AuthUserType;
  saveClientFlags: SaveClientFlags;
  loadPersonaCollection: () => Promise<void>;
  addPersonaCollectionGroup: (group: { group: any }) => void;
  locations: LocationType[];
  onClose: () => void;
  toastIntegration: IntegrationType;
};

export const useOnboarding = ({
  user,
  saveClientFlags,
  loadPersonaCollection,
  addPersonaCollectionGroup,
  locations,
  onClose,
  toastIntegration,
}: Props) => {
  const posFeedbackSentRef = useRef(false);
  const { onboardingFlags, updateOnboardingFlags } = useOnboardingFlags({ user, saveClientFlags });
  const steps = DEFAULT_STEPS_PATH.filter((step) => !onboardingFlags.skipSteps?.[step]);
  const { activeStep, nextStep } = useSteps({
    steps,
    onNextStep: (activeStep, targetStep) => {
      if (!targetStep) {
        return close();
      }

      updateOnboardingFlags(activeStep, targetStep);
    },
  });

  const { pos, posOther } = user.settings.clientFlags;
  const [pointOfSale, setPointOfSale] = useState<PointOfSale | null>(pos as PointOfSale);
  const [pointOfSaleOther, setPointOfSaleOther] = useState<string | null>(posOther);
  const api = useSlingApi();

  const maybeCreateFirstLocation = useCallback(async (): Promise<LocationType | undefined> => {
    if (user.org.isToastSignup) {
      return;
    }

    if (pointOfSale === PointOfSale.toast) {
      return;
    }

    if (locations.length) {
      return;
    }

    await loadPersonaCollection();

    const locationResponse = await api.locations.createLocation({
      type: GroupTypes.location,
      name: user.org.name,
      timezone: user.timezone,
      members: [{ id: user.id }], // add self
    });
    addPersonaCollectionGroup({ group: locationResponse.data });

    return locationResponse.data;
  }, [user]);

  const close = useCallback((): void => {
    const { employeesCount } = user.settings.clientFlags;
    const flags = {
      onboarding: { ...onboardingFlags, finished: true },
      canShowSurvey1: true,
      canShowOTCostsBanner: true,
      canShowMobileAppPopup: true,
    };

    saveClientFlags({ flags });
    maybeCreateFirstLocation();

    if (!posFeedbackSentRef.current && (pointOfSale || pointOfSaleOther)) {
      sendPosFeedback({
        pos: pointOfSale === PointOfSale.none ? "We don't use POS" : pointOfSale,
        posOther: pointOfSaleOther,
        user,
        employeesCount,
      });

      posFeedbackSentRef.current = true;
    }

    onClose();
  }, [
    user,
    onboardingFlags,
    pointOfSale,
    pointOfSaleOther,
    maybeCreateFirstLocation,
    saveClientFlags,
    onClose,
  ]);

  useEffect(() => {
    if (user.org.isToastSignup) {
      setPointOfSale(PointOfSale.toast);
    }
  }, [user.org.isToastSignup]);

  useEffect(() => {
    const { pos, posOther } = user.settings.clientFlags;

    if (pos === pointOfSale && posOther === pointOfSaleOther) {
      return;
    }

    saveClientFlags({ flags: { pos: pointOfSale, posOther: pointOfSaleOther || '' } });
  }, [pointOfSale, pointOfSaleOther]);

  // TODO I would MAYBE replace it with org_settings.onboarding_completed column
  useEffect(() => {
    if (toastIntegration?.enabled) {
      close();
    }
  }, [close, toastIntegration?.enabled]);

  useEffect(() => {
    window.FS?.restart?.();
  });

  useEffect(() => {
    if (user.orgSettings.orgSetupCompleted && activeStep === SetupOrgOnboardingSteps.account) {
      nextStep();
    }
  }, [nextStep, activeStep, user.orgSettings.orgSetupCompleted]);

  // TODO can I maybe get rid of this? or maybe move to useSteps hook?
  useEffect(() => {
    if (steps && !activeStep) {
      nextStep(onboardingFlags.activeStep);
    }
  }, [nextStep, steps, activeStep, onboardingFlags.activeStep]);

  const orgSetupNextStep = (category: string): void => {
    const { isToastSignup, pointOfSale } = user.org;

    if (isToastSignup) {
      nextStep(SetupOrgOnboardingSteps.toastAssist);
      return;
    }

    const step =
      !pointOfSale && TOAST_ONBOARDING_CATEGORIES.includes(category as ToastBusinessCategory)
        ? SetupOrgOnboardingSteps.pos
        : SetupOrgOnboardingSteps.positions;

    nextStep(step);
  };

  const onPointOfSaleSubmit = async (pos: PointOfSale, posOther: string | null) => {
    if (pos === PointOfSale.other) {
      setPointOfSaleOther(posOther);
    }
    setPointOfSale(pos);
    await api.auth.updateOrg(user.org.id, { pointOfSale: posOther || pos });

    nextStep(
      pos === PointOfSale.toast
        ? SetupOrgOnboardingSteps.toastAssist
        : SetupOrgOnboardingSteps.positions
    );
  };

  const onPointOfSaleSkip = () => {
    setPointOfSale(PointOfSale.none);

    nextStep(SetupOrgOnboardingSteps.positions);
  };

  return {
    steps,
    activeStep,
    pointOfSale,
    pointOfSaleOther,
    setPointOfSale,
    setPointOfSaleOther,
    onPointOfSaleSubmit,
    onPointOfSaleSkip,
    nextStep,
    orgSetupNextStep,
    maybeCreateFirstLocation,
    close,
  };
};
